import {
  Button,
  Dialog,
  Input,
  PhoneInput,
  VSpacer,
  ZipInput,
} from '@/components/DesignSystem';
import { AppConfig } from '@/constants/AppConfig';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { CountyApi } from '@/utilities/api/CountyApi';
import { UserApi } from '@/utilities/api/UserApi';
import { UserEndpoint } from '@api/endpoints';
import { ApiUser } from '@api/interfaces';
import HighlightOff from '@mui/icons-material/HighlightOff';
import { Alert, Autocomplete, Stack } from '@mui/material';
import { State } from '@shared/enums';
import {
  EmailRegex,
  getObjectChanges,
  isValidPhoneNumber,
  validatePostalCode,
} from '@shared/utilities';
import { mapValues } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

type EditCustomerDialogProps = {
  customer: ApiUser,
  onClose: () => void,
}

type ValidatedField = 'email' | 'postal' | 'telephone';

const customerDetailQueryKeys = [
  QueryKeys.GET_ENERGY_CUSTOMER,
  QueryKeys.GET_FARMERS,
  QueryKeys.GET_LINKED_ACCOUNTS,
  QueryKeys.GET_RETAILER_ASSOCIATED_FARMERS,
  QueryKeys.GET_RETAILER_FARMERS_BY_ID,
  QueryKeys.GET_USER,
  QueryKeys.LIST_CUSTOMER_OVERVIEW,
  QueryKeys.LIST_PRIMARY_CUSTOMERS,
  QueryKeys.LIST_TIER_CUSTOMERS,
  QueryKeys.LIST_USERS,
];

type CustomerForm = {
  address1: string,
  address2: string,
  businessName: string,
  city: string,
  countyId: string | null,
  email: string,
  firstName: string,
  lastName: string,
  postal: string,
  state: State | null,
  telephone: string,
};

const toCustomerForm = (customer: ApiUser): CustomerForm => ({
  address1: customer.address1 ?? '',
  address2: customer.address2 ?? '',
  businessName: customer.businessName ?? '',
  city: customer.city ?? '',
  countyId: customer.countyId ?? null,
  email: customer.email ?? '',
  firstName: customer.firstName ?? '',
  lastName: customer.lastName ?? '',
  postal: customer.postal ?? '',
  state: customer.state ?? null,
  telephone: customer.telephone ?? '',
});

const trimCustomerForm = (form: CustomerForm): CustomerForm => (
  mapValues(form, (value) => (typeof value === 'string' ? value.trim() : value)) as CustomerForm
);

const buildUpdateRequest = (
  original: CustomerForm,
  edited: CustomerForm,
): UserEndpoint.Update.Request => {
  const { email, telephone, ...changes } = getObjectChanges(original, edited);
  const clearedFieldsAsNull = mapValues(changes, (value) => value || null);
  return {
    ...clearedFieldsAsNull,
    ...(email ? { email } : {}),
    ...(telephone ? { telephone } : {}),
  } as UserEndpoint.Update.Request;
};

export const EditCustomerDialog = ({
  customer,
  onClose,
}: EditCustomerDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const [originalForm] = useState(() => trimCustomerForm(toCustomerForm(customer)));
  const [form, setForm] = useState(originalForm);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: counties, isFetching: isFetchingCounties } = useQuery(
    [QueryKeys.LIST_COUNTIES, form.state],
    () => CountyApi.list({ state: form.state! }),
    { enabled: !!form.state },
  );

  const editedForm = trimCustomerForm(form);
  const updateRequest = buildUpdateRequest(originalForm, editedForm);
  const hasChanges = Object.keys(updateRequest).length > 0;

  const getFieldError = (
    field: ValidatedField,
    isValidValue: (value: string) => boolean,
    invalidMessage: string,
    canBeCleared: boolean,
  ) => {
    const value = editedForm[field];
    if (value === originalForm[field]) {
      return '';
    }
    if (!value) {
      return canBeCleared ? '' : t($ => $.customers.details.editCustomer.cannotClear);
    }
    return isValidValue(value) ? '' : invalidMessage;
  };

  const fieldErrors: Record<ValidatedField, string> = {
    email: getFieldError(
      'email',
      (email) => EmailRegex.test(email),
      t($ => $.customers.details.editCustomer.invalidEmail),
      false,
    ),
    postal: getFieldError(
      'postal',
      validatePostalCode,
      t($ => $.customers.details.editCustomer.invalidPostalCode),
      true,
    ),
    telephone: getFieldError(
      'telephone',
      (telephone) => isValidPhoneNumber(telephone, AppConfig.env.test),
      t($ => $.customers.details.editCustomer.invalidPhone),
      false,
    ),
  };
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
  const isValid = hasChanges && !hasFieldErrors;

  const { isLoading: isSaving, mutate: saveCustomer } = useMutation(
    () => UserApi.update(customer.id, updateRequest),
    {
      onError: (error: Error) => {
        setErrorMessage(error.message || t($ => $.common.error.genericError));
      },
      onSuccess: async () => {
        await Promise.all(customerDetailQueryKeys.map((queryKey) => (
          queryClient.invalidateQueries(queryKey)
        )));
        openSnackbar(t($ => $.customers.details.editCustomer.success));
        onClose();
      },
    },
  );

  const updateField = <K extends keyof CustomerForm>(field: K, value: CustomerForm[K]) => {
    setErrorMessage('');
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const selectedCounty = counties?.find((county) => county.id === form.countyId) ?? null;

  return (
    <Dialog
      acceptButton={() => (
        <Button
          disabled={!isValid}
          loading={isSaving}
          onClick={() => saveCustomer()}
          testID="edit-customer-save-button"
          variant="contained"
        >
          {t($ => $.common.actions.save)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button
          onClick={onClose}
          {...props}
          color="inherit"
          testID="edit-customer-cancel-button"
        >
          {t($ => $.common.actions.cancel)}
        </Button>
      )}
      onClose={onClose}
      open
      showCloseButton={false}
      testID="edit-customer-dialog"
      title={t($ => $.customers.details.editCustomer.title)}
    >
      {!!errorMessage && (
        <>
          <Alert color="error" icon={false}>{errorMessage}</Alert>
          <VSpacer size="4" />
        </>
      )}
      <Stack spacing={4}>
        <Input
          label={t($ => $.customers.details.editCustomer.firstName)}
          onChangeText={(value) => updateField('firstName', value)}
          testID="edit-customer-first-name"
          value={form.firstName}
        />
        <Input
          label={t($ => $.customers.details.editCustomer.lastName)}
          onChangeText={(value) => updateField('lastName', value)}
          testID="edit-customer-last-name"
          value={form.lastName}
        />
        <Input
          label={t($ => $.customers.details.editCustomer.businessName)}
          onChangeText={(value) => updateField('businessName', value)}
          testID="edit-customer-business-name"
          value={form.businessName}
        />
        <PhoneInput
          error={!!fieldErrors.telephone}
          helperText={fieldErrors.telephone}
          label={t($ => $.customers.details.editCustomer.mobilePhone)}
          onChangeText={(value) => updateField('telephone', value)}
          required={!!originalForm.telephone}
          testID="edit-customer-telephone"
          value={form.telephone}
        />
        <Input
          error={!!fieldErrors.email}
          helperText={fieldErrors.email}
          label={t($ => $.customers.details.editCustomer.email)}
          onChangeText={(value) => updateField('email', value)}
          required={!!originalForm.email}
          testID="edit-customer-email"
          value={form.email}
        />
        <Input
          label={t($ => $.customers.details.editCustomer.address1)}
          onChangeText={(value) => updateField('address1', value)}
          testID="edit-customer-address1"
          value={form.address1}
        />
        <Input
          label={t($ => $.customers.details.editCustomer.address2)}
          onChangeText={(value) => updateField('address2', value)}
          testID="edit-customer-address2"
          value={form.address2}
        />
        <Input
          label={t($ => $.customers.details.editCustomer.city)}
          onChangeText={(value) => updateField('city', value)}
          testID="edit-customer-city"
          value={form.city}
        />
        <Autocomplete
          clearIcon={<HighlightOff />}
          disablePortal
          onChange={(_, state) => {
            updateField('state', state);
            updateField('countyId', null);
          }}
          options={Object.values(State)}
          renderInput={(params) => (
            <Input
              {...params}
              label={t($ => $.customers.details.editCustomer.state)}
              testID="edit-customer-state"
            />
          )}
          value={form.state}
        />
        <Autocomplete
          clearIcon={<HighlightOff />}
          disablePortal
          disabled={!form.state}
          getOptionLabel={(county) => county.county}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={isFetchingCounties}
          onChange={(_, county) => updateField('countyId', county?.id ?? null)}
          options={counties ?? []}
          renderInput={(params) => (
            <Input
              {...params}
              label={t($ => $.customers.details.editCustomer.county)}
              testID="edit-customer-county"
            />
          )}
          value={selectedCounty}
        />
        <ZipInput
          error={!!fieldErrors.postal}
          helperText={fieldErrors.postal}
          label={t($ => $.customers.details.editCustomer.postalCode)}
          onChangeText={(value) => updateField('postal', value)}
          testID="edit-customer-postal"
          value={form.postal}
        />
      </Stack>
    </Dialog>
  );
};
